/**
 * Application route constants
 * Centralized route definitions for the FortifyTech application
 */

export const ROUTES = {
  // Public routes
  HOME: '/',
  ABOUT: '/about',
  SERVICES: '/services',
  IT_SOLUTIONS: '/services/it-solutions',
  SOFTWARE_DEVELOPMENT: '/services/software-development',
  CONTACT: '/contact',
  PRIVACY_POLICY: '/privacy',
  TERMS_OF_SERVICE: '/terms',
  COMPLIANCE: '/compliance',
  SECURITY: '/security',
  CONTACT_SUPPORT: '/support',
  CAREERS: '/careers',
  BLOG: '/blog',
  BLOG_DETAIL: '/blog/:slug',

  // Authentication routes
  LOGIN: '/login',
  REGISTER: '/register',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',

  // Dashboard routes
  DASHBOARD: '/dashboard',
  DASHBOARD_OVERVIEW: '/dashboard/overview',

  // Security services routes
  SECURITY_AUDIT: '/dashboard/security-audit',
  VULNERABILITY_SCAN: '/dashboard/vulnerability-scan',
  COMPLIANCE_CHECK: '/dashboard/compliance-check',
  PENETRATION_TEST: '/dashboard/penetration-test',

  // Application routes
  NOTIFICATIONS: '/dashboard/notifications',

  // Reports routes
  REPORTS: '/dashboard/reports',
  REPORTS_SECURITY: '/dashboard/reports/security',
  REPORTS_COMPLIANCE: '/dashboard/reports/compliance',
  REPORTS_VULNERABILITY: '/dashboard/reports/vulnerability',

  // Settings routes
  SETTINGS: '/dashboard/settings',
  SETTINGS_PROFILE: '/dashboard/settings/profile',
  SETTINGS_SECURITY: '/dashboard/settings/security',
  SETTINGS_BILLING: '/dashboard/settings/billing',
  SETTINGS_TEAM: '/dashboard/settings/team',

  // Help & Support routes
  HELP: '/help',
  DOCUMENTATION: '/help/documentation',
  FAQ: '/help/faq',
  SUPPORT: '/help/support',

  // Legal routes
  DELETE_DATA: '/delete-data',

  // Error pages
  NOT_FOUND: '/404',
  UNAUTHORIZED: '/401',
  FORBIDDEN: '/403',
  SERVER_ERROR: '/500',
} as const;

export type RouteKey = keyof typeof ROUTES;
export type RouteValue = (typeof ROUTES)[RouteKey];

/**
 * Route groups for easier navigation management
 */
export const ROUTE_GROUPS = {
  PUBLIC: [
    ROUTES.HOME,
    ROUTES.ABOUT,
    ROUTES.SERVICES,
    ROUTES.CONTACT,
    ROUTES.PRIVACY_POLICY,
    ROUTES.TERMS_OF_SERVICE,
    ROUTES.COMPLIANCE,
    ROUTES.SECURITY,
    ROUTES.CONTACT_SUPPORT,
    ROUTES.DELETE_DATA,
  ],
  AUTH: [
    ROUTES.LOGIN,
    ROUTES.REGISTER,
    ROUTES.FORGOT_PASSWORD,
    ROUTES.RESET_PASSWORD,
  ],
  DASHBOARD: [
    ROUTES.DASHBOARD,
    ROUTES.DASHBOARD_OVERVIEW,
    ROUTES.SECURITY_AUDIT,
    ROUTES.VULNERABILITY_SCAN,
    ROUTES.COMPLIANCE_CHECK,
    ROUTES.PENETRATION_TEST,
    ROUTES.NOTIFICATIONS,
  ],
  REPORTS: [
    ROUTES.REPORTS,
    ROUTES.REPORTS_SECURITY,
    ROUTES.REPORTS_COMPLIANCE,
    ROUTES.REPORTS_VULNERABILITY,
  ],
  SETTINGS: [
    ROUTES.SETTINGS,
    ROUTES.SETTINGS_PROFILE,
    ROUTES.SETTINGS_SECURITY,
    ROUTES.SETTINGS_BILLING,
    ROUTES.SETTINGS_TEAM,
  ],
  HELP: [ROUTES.HELP, ROUTES.DOCUMENTATION, ROUTES.FAQ, ROUTES.SUPPORT],
  ERROR: [
    ROUTES.NOT_FOUND,
    ROUTES.UNAUTHORIZED,
    ROUTES.FORBIDDEN,
    ROUTES.SERVER_ERROR,
  ],
} as const;

/**
 * Protected routes that require authentication
 */
export const PROTECTED_ROUTES = [
  ...ROUTE_GROUPS.DASHBOARD,
  ...ROUTE_GROUPS.REPORTS,
  ...ROUTE_GROUPS.SETTINGS,
] as const;

/**
 * Route metadata for navigation and breadcrumbs
 */
export const ROUTE_METADATA = {
  [ROUTES.HOME]: { title: 'Home', breadcrumb: 'Home' },
  [ROUTES.ABOUT]: { title: 'About', breadcrumb: 'About' },
  [ROUTES.SERVICES]: { title: 'Services', breadcrumb: 'Services' },
  [ROUTES.CONTACT]: { title: 'Contact', breadcrumb: 'Contact' },
  [ROUTES.LOGIN]: { title: 'Login', breadcrumb: 'Login' },
  [ROUTES.REGISTER]: { title: 'Register', breadcrumb: 'Register' },
  [ROUTES.DASHBOARD]: { title: 'Dashboard', breadcrumb: 'Dashboard' },
  [ROUTES.DASHBOARD_OVERVIEW]: {
    title: 'Dashboard Overview',
    breadcrumb: 'Overview',
  },
  [ROUTES.SECURITY_AUDIT]: {
    title: 'Security Audit',
    breadcrumb: 'Security Audit',
  },
  [ROUTES.VULNERABILITY_SCAN]: {
    title: 'Vulnerability Scan',
    breadcrumb: 'Vulnerability Scan',
  },
  [ROUTES.COMPLIANCE_CHECK]: {
    title: 'Compliance Check',
    breadcrumb: 'Compliance Check',
  },
  [ROUTES.PENETRATION_TEST]: {
    title: 'Penetration Test',
    breadcrumb: 'Penetration Test',
  },
  [ROUTES.REPORTS]: { title: 'Reports', breadcrumb: 'Reports' },
  [ROUTES.NOTIFICATIONS]: { title: 'Notifications', breadcrumb: 'Notifications' },
  [ROUTES.SETTINGS]: { title: 'Settings', breadcrumb: 'Settings' },
  [ROUTES.HELP]: { title: 'Help', breadcrumb: 'Help' },
} as const;